// Registry of addable blocks for the Builder2 prototype.
// Each entry's defaultContent is lifted from the garden03 template
// (backend/routes/templates.js) so adding a block starts from real,
// sensible copy instead of empty fields.
import NavBlock from '@/components/blocks2/NavBlock.vue'
import HeroVideoBlock from '@/components/blocks2/HeroVideoBlock.vue'
import ServicesGridBlock from '@/components/blocks2/ServicesGridBlock.vue'
import PricingBlock from '@/components/blocks2/PricingBlock.vue'
import AboutBlock from '@/components/blocks2/AboutBlock.vue'
import GalleryBlock from '@/components/blocks2/GalleryBlock.vue'
import GalleryShowcaseBlock from '@/components/blocks2/GalleryShowcaseBlock.vue'
import TestimonialsBlock from '@/components/blocks2/TestimonialsBlock.vue'
import ContactBlock from '@/components/blocks2/ContactBlock.vue'
import FooterBlock from '@/components/blocks2/FooterBlock.vue'

function styleVariants(type, component, labels) {
  return labels.map((label, index) => ({
    id: `${type}-${String(index + 1).padStart(2, '0')}`,
    label: `${type[0].toUpperCase()}${type.slice(1)} ${String(index + 1).padStart(2, '0')} — ${label}`,
    component,
  }))
}

export const blockRegistry = {
  nav: {
    component: NavBlock,
    label: 'Navigation',
    icon: 'mdi-menu',
    defaultVariant: 'nav-01',
    variants: styleVariants('nav', NavBlock, ['Classic', 'Light', 'Centered']),
    defaultContent: {
      nav_logo: { type: 'image', label: 'Logo', value: 'Logo' },
      nav_links: {
        type: 'array:text',
        label: 'Navigation Links',
        value: ['Home', 'Services', 'About', 'Gallery', 'Testimonials', 'Contact'],
      },
    },
  },

  'hero-video': {
    component: HeroVideoBlock,
    label: 'Hero (Video)',
    icon: 'mdi-image-outline',
    defaultVariant: 'hero-01',
    variants: styleVariants('hero', HeroVideoBlock, ['Centered', 'Editorial', 'Panel']),
    defaultContent: {
      hero_video_url: {
        type: 'video',
        label: 'Hero Video',
        value: 'https://www.pexels.com/download/video/35083678/',
      },
      hero_kicker: { type: 'text', label: 'Kicker', value: 'Professional Services' },
      hero_headline: { type: 'text', label: 'Headline', value: 'A Headline That Grabs Attention' },
      hero_sub: {
        type: 'textarea',
        label: 'Subheading',
        value: 'A short line of supporting copy that explains what you do and why it matters.',
      },
      hero_btn_1: { type: 'text', label: 'Primary Button', value: 'Our Services' },
      hero_btn_2: { type: 'text', label: 'Secondary Button', value: 'Get a Quote' },
    },
  },

  services: {
    component: ServicesGridBlock,
    label: 'Services Grid',
    icon: 'mdi-view-grid-outline',
    defaultVariant: 'services-01',
    variants: styleVariants('services', ServicesGridBlock, ['Cards', 'Rows', 'Dark']),
    defaultContent: {
      label: { type: 'text', label: 'Section Label', value: 'What We Do' },
      title: { type: 'text', label: 'Section Title', value: 'Expert Services For Every Job' },
      sub: {
        type: 'textarea',
        label: 'Subheading',
        value: 'From a single task to a full project, we bring the same care and craft to every job.',
      },
      items: {
        type: 'array:object',
        label: 'Service Cards',
        value: [
          { icon: 'mdi-star-outline', title: 'Service One', description: 'A short description of the first service you offer.' },
          { icon: 'mdi-star-outline', title: 'Service Two', description: 'A short description of the second service you offer.' },
          { icon: 'mdi-star-outline', title: 'Service Three', description: 'A short description of the third service you offer.' },
        ],
      },
    },
  },

  pricing: {
    component: PricingBlock,
    label: 'Pricing',
    icon: 'mdi-currency-usd',
    defaultVariant: 'pricing-01',
    variants: styleVariants('pricing', PricingBlock, ['Solid', 'Outline', 'List']),
    defaultContent: {
      label: { type: 'text', label: 'Section Label', value: 'Pricing' },
      title: { type: 'text', label: 'Section Title', value: 'Simple, Transparent Pricing' },
      sub: { type: 'textarea', label: 'Subheading', value: 'Pricing starts here — get in touch for an exact quote.' },
      items: {
        type: 'array:object',
        label: 'Pricing Items',
        value: [
          { name: 'Basic', price: '$99', note: '' },
          { name: 'Standard', price: '$199', note: '' },
          { name: 'Premium', price: 'From $299', note: 'Ask us for details' },
        ],
      },
      footer_note: {
        type: 'textarea',
        label: 'Footer Note',
        value: 'Pricing may vary depending on your location and requirements — get in touch for an exact quote.',
      },
    },
  },

  about: {
    component: AboutBlock,
    label: 'About',
    icon: 'mdi-information-outline',
    defaultVariant: 'about-01',
    variants: styleVariants('about', AboutBlock, ['Image Left', 'Image Right', 'Editorial']),
    defaultContent: {
      label: { type: 'text', label: 'Section Label', value: 'About Us' },
      title: { type: 'text', label: 'Section Title', value: 'Our Story' },
      body_1: { type: 'textarea', label: 'Body Paragraph 1', value: 'A short paragraph introducing your business and its history.' },
      body_2: { type: 'textarea', label: 'Body Paragraph 2', value: 'A second paragraph about how you work with clients.' },
      points: {
        type: 'array:text',
        label: 'Key Points',
        value: ['Licensed & insured', 'Free consultation', 'Custom quotes'],
      },
      image: {
        type: 'image',
        label: 'About Image',
        value: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=700&q=80',
      },
      badge_number: { type: 'text', label: 'Badge Number', value: '10+' },
      badge_label: { type: 'text', label: 'Badge Label', value: 'Years of Experience' },
    },
  },

  gallery: {
    component: GalleryBlock,
    label: 'Gallery',
    icon: 'mdi-image-multiple-outline',
    defaultVariant: 'gallery-01',
    variants: [
      { id: 'gallery-01', label: 'Gallery 01 — Mosaic', component: GalleryBlock },
      { id: 'gallery-02', label: 'Gallery 02 — Showcase', component: GalleryShowcaseBlock },
      { id: 'gallery-03', label: 'Gallery 03 — Filmstrip', component: GalleryBlock },
    ],
    defaultContent: {
      label: { type: 'text', label: 'Section Label', value: 'Our Work' },
      title: { type: 'text', label: 'Section Title', value: 'A Few Past Projects' },
      images: {
        type: 'array:object',
        label: 'Gallery Images',
        value: [
          { url: 'https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?w=600&q=80', caption: 'Project one', size: 'tall' },
          { url: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&q=80', caption: 'Project two', size: '' },
          { url: 'https://images.unsplash.com/photo-1416879595882-3373a0480b5b?w=600&q=80', caption: 'Project three', size: '' },
        ],
      },
    },
  },

  testimonials: {
    component: TestimonialsBlock,
    label: 'Testimonials',
    icon: 'mdi-format-quote-close',
    defaultVariant: 'testimonials-01',
    variants: styleVariants('testimonials', TestimonialsBlock, ['Dark Cards', 'Light Cards', 'Minimal']),
    defaultContent: {
      label: { type: 'text', label: 'Section Label', value: 'Client Stories' },
      title: { type: 'text', label: 'Section Title', value: 'What Our Clients Say' },
      items: {
        type: 'array:object',
        label: 'Testimonials',
        value: [
          { name: 'Jane Smith', location: 'Toronto, ON', quote: 'Fantastic work from start to finish. Highly recommended.' },
          { name: 'John Doe', location: 'Ottawa, ON', quote: 'Professional, on time, and great communication throughout.' },
        ],
      },
    },
  },

  contact: {
    component: ContactBlock,
    label: 'Contact',
    icon: 'mdi-email-outline',
    defaultVariant: 'contact-01',
    variants: styleVariants('contact', ContactBlock, ['Split', 'Dark', 'Centered']),
    defaultContent: {
      label: { type: 'text', label: 'Section Label', value: 'Get In Touch' },
      title: { type: 'text', label: 'Section Title', value: "Let's Talk" },
      sub: { type: 'textarea', label: 'Subheading', value: "Send us a message and we'll get back to you within one business day." },
      email: { type: 'email', label: 'Email', value: 'contact@example.com' },
      phone: { type: 'text', label: 'Phone', value: 'Toll Free: +1 212-212-2376' },
      hours: { type: 'text', label: 'Hours', value: 'Mon – Fri: 9:00 – 17:00' },
    },
  },

  footer: {
    component: FooterBlock,
    label: 'Footer',
    icon: 'mdi-page-layout-footer',
    defaultVariant: 'footer-01',
    variants: styleVariants('footer', FooterBlock, ['Classic', 'Light', 'Centered']),
    defaultContent: {
      footer_logo: { type: 'text', label: 'Logo', value: 'Logo' },
      tagline: { type: 'textarea', label: 'Tagline', value: 'A short tagline about your business.' },
      footer_disclaimer: { type: 'text', label: 'Disclaimer', value: `© Copyright ${new Date().getFullYear()}. All rights reserved.` },
    },
  },
}

export const blockOrder = [
  'nav', 'hero-video', 'services', 'pricing', 'about', 'gallery', 'testimonials', 'contact', 'footer',
]

export function getBlockVariants(type) {
  const entry = blockRegistry[type]
  if (!entry) return []

  return entry.variants ?? [
    { id: 'default', label: `${entry.label} 01`, component: entry.component },
  ]
}

export function getBlockComponent(type, variant) {
  const entry = blockRegistry[type]
  const variants = getBlockVariants(type)
  return variants.find(item => item.id === variant)?.component
    ?? variants[0]?.component
    ?? entry?.component
}
